import React from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../../utils';
import { Home, Users, FolderOpen, CreditCard, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { moduleLabels, moduleColors } from './menuConfig';
import { usePermissions } from '../../hooks/usePermissions';

interface ModuleSwitcherProps {
  activeModule: string;
}

// Define module access permissions
const modulePermissions: Record<string, string[]> = {
  hris: [
    'hris_emp.employee.view',
    'hris_org.organization.view',
    'hris_att.attendance.view',
    'hris_leave.leave.view',
    'hris_kpi.kpi.view',
    'hris_payroll.payroll.view',
    'hris_recruit.recruitment.view',
    'hris_dev.development.view',
    'hris_punish.punishment.view',
    'hris_role_access.role_access.view',
  ],
  credit: [
    'credit.submission.view',
    'credit.application.view',
    'credit.scoring.view',
    'credit.approval.view',
  ],
  archive: ['archive.general.view', 'archive.category.view', 'archive.search.view'],
  debtor_legacy: ['debtor.debtor.view', 'debtor.document.view', 'debtor.validation.view'],
};

export default function ModuleSwitcher({ activeModule }: ModuleSwitcherProps) {
  const navigate = useNavigate();
  const { hasAnyPermission, isAdmin } = usePermissions();

  // Fallback if activeModule is not valid
  const validModule = activeModule && moduleLabels[activeModule] ? activeModule : 'portal';
  const moduleColor = moduleColors[validModule] || moduleColors.portal;
  const moduleLabel = moduleLabels[validModule] || moduleLabels.portal;

  // Check if user has access to a module
  const hasModuleAccess = (module: string): boolean => {
    if (isAdmin) return true; // Admin can access all modules
    if (module === 'portal') return true; // Portal is always accessible

    const permissions = modulePermissions[module];
    if (!permissions) return true; // No permissions defined, allow access

    const hasAccess = hasAnyPermission(permissions);

    return hasAccess;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`${moduleColor} text-white hover:opacity-90 hidden sm:flex items-center px-4 py-2 rounded-md font-medium transition-all duration-300`}
        >
          {moduleLabel}
          <ChevronDown className="w-4 h-4 ml-2" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate(createPageUrl('Portal'))}>
          <Home className="w-4 h-4 mr-2" /> Portal Utama
        </DropdownMenuItem>

        {hasModuleAccess('hris') && (
          <DropdownMenuItem onClick={() => navigate(createPageUrl('HRISDashboard'))}>
            <Users className="w-4 h-4 mr-2" /> HRIS
          </DropdownMenuItem>
        )}

        {hasModuleAccess('archive') && (
          <DropdownMenuItem onClick={() => navigate(createPageUrl('ArchiveDashboard'))}>
            <FolderOpen className="w-4 h-4 mr-2" /> Arsip Terintegrasi
          </DropdownMenuItem>
        )}

        {hasModuleAccess('credit') && (
          <DropdownMenuItem onClick={() => navigate(createPageUrl('CreditDashboard'))}>
            <CreditCard className="w-4 h-4 mr-2" /> Analisis Kredit
          </DropdownMenuItem>
        )}

        {(hasModuleAccess('debtor_legacy') ||
          hasModuleAccess('hris') ||
          hasModuleAccess('archive') ||
          hasModuleAccess('credit')) && <DropdownMenuSeparator />}

        {hasModuleAccess('debtor_legacy') && (
          <DropdownMenuItem
            onClick={() => navigate(createPageUrl('DebtorDashboard'))}
            className="text-amber-600"
          >
            <FolderOpen className="w-4 h-4 mr-2" /> ⚠️ Legacy: Arsip Debitur
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
